import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import toast from 'react-hot-toast';
import {
  AlertCircle,
  Pill,
  HelpCircle,
  ChevronRight,
  X,
  CheckCircle,
  Loader2,
  Calendar,
  Truck,
  Zap,
  Phone
} from 'lucide-react';

const QUICK_OPTIONS = [
  {
    id: 'not_well',
    label: "I don't feel well",
    icon: AlertCircle,
    color: 'bg-danger-100 text-danger-600 border-danger-200',
    hoverColor: 'hover:bg-danger-50',
    description: 'New symptoms, pain, or feeling worse',
    hint: 'e.g. shortness of breath, swelling, chest pain, dizziness',
    defaultSeverity: 'medium',
  },
  {
    id: 'medication',
    label: "I can't get my medication",
    icon: Pill,
    color: 'bg-amber-100 text-amber-600 border-amber-200',
    hoverColor: 'hover:bg-amber-50',
    description: 'Ran out, pharmacy problem, or can\'t afford it',
    hint: 'e.g. pharmacy is out of stock, I ran out, too expensive',
    defaultSeverity: 'high',
  },
  {
    id: 'medication_side_effect',
    label: 'My medication is causing problems',
    icon: Zap,
    color: 'bg-orange-100 text-orange-600 border-orange-200',
    hoverColor: 'hover:bg-orange-50',
    description: 'Side effects, allergic reaction, or wrong dose',
    hint: 'e.g. nausea, rash, feeling dizzy after taking it',
    defaultSeverity: 'high',
    backendType: 'medication',
  },
  {
    id: 'service_missed',
    label: 'A nurse or care visit didn\'t happen',
    icon: Truck,
    color: 'bg-purple-100 text-purple-600 border-purple-200',
    hoverColor: 'hover:bg-purple-50',
    description: 'Home nurse, aide, or delivery didn\'t arrive',
    hint: 'e.g. my home nurse didn\'t show up, medication wasn\'t delivered',
    defaultSeverity: 'medium',
  },
  {
    id: 'appointment',
    label: 'I missed or need to reschedule an appointment',
    icon: Calendar,
    color: 'bg-blue-100 text-blue-600 border-blue-200',
    hoverColor: 'hover:bg-blue-50',
    description: 'Couldn\'t make it or need a new time',
    hint: 'e.g. I couldn\'t get a ride, I forgot, I need a different day',
    defaultSeverity: 'medium',
  },
  {
    id: 'confused',
    label: "I don't know what to do",
    icon: HelpCircle,
    color: 'bg-primary-100 text-primary-600 border-primary-200',
    hoverColor: 'hover:bg-primary-50',
    description: 'Confused about instructions or need guidance',
    hint: 'e.g. not sure how to take this medication, conflicting instructions',
    defaultSeverity: 'medium',
  },
  {
    id: 'custom',
    label: 'Something else',
    icon: Phone,
    color: 'bg-surface-100 text-surface-600 border-surface-200',
    hoverColor: 'hover:bg-surface-50',
    description: 'Any other concern you want your care team to know',
    hint: 'Describe what\'s happening in your own words',
    defaultSeverity: 'medium',
  },
];

const SEVERITY_OPTIONS = [
  { value: 'low',    label: 'Not urgent',      color: 'bg-success-100 text-success-700 border-success-300' },
  { value: 'medium', label: 'Concerning',       color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
  { value: 'high',   label: 'Needs attention',  color: 'bg-orange-100 text-orange-700 border-orange-300' },
  { value: 'urgent', label: 'Urgent',            color: 'bg-danger-100 text-danger-700 border-danger-300' },
];

const RESPONSE_TIME = { urgent: 'Immediate', high: 'Within 2 hours', medium: 'Within 24 hours', low: 'Within 48 hours' };

const NEXT_STEPS = {
  urgent: [
    'A care team member has been alerted right now.',
    'Someone will call you within the hour.',
    'If you feel worse before then, call 911.',
  ],
  high: [
    'Your care team will review this within 2 hours.',
    'Keep your phone nearby.',
    'If symptoms worsen, tap "Report an Issue" again and choose Urgent.',
  ],
  medium: [
    'Your care team will follow up within 24 hours.',
    'You can add more details anytime from your dashboard.',
  ],
  low: [
    'Your care team will review this and follow up.',
    'Keep notes on any changes.',
  ],
};

const QuickCaptureWidget = ({ onEventCreated, compact = false, preselectedType = null, embedded = false }) => {
  const [selectedOption, setSelectedOption] = useState(preselectedType);
  const [details, setDetails] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdEventId, setCreatedEventId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (preselectedType) setSelectedOption(preselectedType);
  }, [preselectedType]);

  const activeOption = QUICK_OPTIONS.find(o => o.id === selectedOption);

  const handleOptionSelect = (optionId) => {
    const opt = QUICK_OPTIONS.find(o => o.id === optionId);
    setSelectedOption(optionId);
    setDetails('');
    setSeverity(opt?.defaultSeverity || 'medium');
  };

  const handleClose = () => {
    setSelectedOption(null);
    setDetails('');
    setSeverity('medium');
    setShowSuccess(false);
  };

  const handleSubmit = async () => {
    if (!selectedOption) return;
    setIsSubmitting(true);
    try {
      const backendType = activeOption?.backendType || selectedOption;
      const response = await api.post('/care-events/quick-capture', {
        type: backendType,
        details: details.trim() || activeOption?.description,
        severity,
      });
      if (response.data.success) {
        setCreatedEventId(response.data.data?._id || null);
        setShowSuccess(true);
        if (onEventCreated) onEventCreated(response.data.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit report');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Success screen ──────────────────────────────────────────────────────────
  if (showSuccess) {
    const nextSteps = NEXT_STEPS[severity] || NEXT_STEPS.medium;
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-success-200 p-7">
        <div className="text-center mb-5">
          <div className="w-14 h-14 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <CheckCircle className="w-7 h-7 text-success-600" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-1">Report Received</h3>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-surface-100 rounded-full text-sm text-slate-600">
            <span className="w-2 h-2 bg-success-500 rounded-full animate-pulse" />
            Response time: {RESPONSE_TIME[severity]}
          </div>
        </div>

        {/* What happens next */}
        <div className="bg-surface-50 rounded-xl p-4 mb-5">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">What happens next</p>
          <ul className="space-y-2">
            {nextSteps.map((step, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                {step}
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          {createdEventId && (
            <button
              onClick={() => navigate(`/care-events/${createdEventId}`)}
              className="flex-1 px-4 py-2.5 bg-primary text-white rounded-full text-sm font-semibold hover:bg-primary/90 transition-all"
            >
              View Report
            </button>
          )}
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-2.5 border border-outline-variant/20 text-slate-700 rounded-full text-sm font-semibold hover:bg-surface-50 transition-all"
          >
            Report Another Issue
          </button>
        </div>
      </div>
    );
  }

  // ── Compact trigger ─────────────────────────────────────────────────────────
  if (compact && !selectedOption) {
    return (
      <button
        onClick={() => setSelectedOption('selection')}
        className="w-full p-4 bg-gradient-to-r from-primary to-primary-container text-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98]"
      >
        <div className="flex items-center justify-center gap-3">
          <AlertCircle className="w-6 h-6" />
          <span className="font-semibold text-lg">Report an Issue</span>
          <ChevronRight className="w-5 h-5" />
        </div>
        <p className="text-primary-100 text-sm mt-1">Quickly let us know what you need</p>
      </button>
    );
  }

  // ── Main widget ─────────────────────────────────────────────────────────────
  return (
    <div className={`bg-white rounded-2xl border border-outline-variant/10 overflow-hidden ${embedded ? '' : 'shadow-premium'}`}>
      {/* Header — hidden when embedded inline on dashboard */}
      {!embedded && (
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-4 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-lg">
              {selectedOption ? 'Add Details' : "What's going on?"}
            </h2>
            <p className="text-slate-300 text-sm">
              {selectedOption ? 'Help us understand better' : 'Tap one option — takes 30 seconds'}
            </p>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {!selectedOption ? (
        /* ── Option list ── */
        <div className="p-4">
          <div className="flex flex-col gap-2.5">
            {QUICK_OPTIONS.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.id}
                  onClick={() => handleOptionSelect(option.id)}
                  className={`p-4 rounded-xl border-2 transition-all text-left flex items-center gap-4
                    ${option.color} ${option.hoverColor} hover:shadow-md active:scale-[0.98]`}
                >
                  <Icon className="w-7 h-7 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm leading-tight">{option.label}</div>
                    <div className="text-xs opacity-70 mt-0.5">{option.description}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 opacity-40 shrink-0" />
                </button>
              );
            })}
          </div>
          <div className="mt-4 p-3 bg-danger-50 rounded-lg border border-danger-100">
            <p className="text-sm text-danger-700 text-center">
              <span className="font-semibold">Life-threatening emergency?</span> Call 911 immediately
            </p>
          </div>
        </div>
      ) : (
        /* ── Detail input ── */
        <div className="p-4 space-y-4">
          {/* Selected option chip */}
          {activeOption && (() => {
            const Icon = activeOption.icon;
            return (
              <div className={`flex items-center gap-3 p-3 rounded-xl border-2 ${activeOption.color}`}>
                <Icon className="w-5 h-5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{activeOption.label}</div>
                </div>
                <button
                  onClick={() => setSelectedOption(null)}
                  className="text-xs font-semibold opacity-70 hover:opacity-100"
                >
                  Change
                </button>
              </div>
            );
          })()}

          {/* Severity */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              How urgent is this?
            </label>
            <div className="grid grid-cols-2 gap-2">
              {SEVERITY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSeverity(opt.value)}
                  className={`p-2.5 rounded-xl border-2 text-sm font-medium transition-all
                    ${severity === opt.value
                      ? opt.color + ' border-current ring-1 ring-current'
                      : 'bg-surface-50 border-outline-variant/10 text-slate-600 hover:bg-surface-100'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Details */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">
              Any details? <span className="font-normal text-slate-400">(optional)</span>
            </label>
            {activeOption?.hint && (
              <p className="text-xs text-slate-500 mb-1.5">{activeOption.hint}</p>
            )}
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }}
              placeholder="Tell us more..."
              className="w-full p-3 border border-outline-variant/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary min-h-[80px] resize-none bg-surface-50 focus:bg-white transition-all text-sm"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1 py-3 px-4 border-2 border-outline-variant/20 text-slate-700 rounded-full font-semibold hover:bg-surface-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1 py-3 px-4 bg-gradient-to-r from-primary to-primary-container text-white rounded-full font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Sending...</>
              ) : (
                <>Send Report <ChevronRight className="w-4 h-4" /></>
              )}
            </button>
          </div>

          <div className="p-3 bg-danger-50 border border-danger-100 rounded-xl">
            <p className="text-sm text-danger-700 text-center">
              <span className="font-semibold">Medical emergency?</span> Call 911 immediately
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickCaptureWidget;
